
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import type { FeedbackType } from "./types";

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const [type, setType] = useState<FeedbackType>("general");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user } = useAuth();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("feedback")
        .insert([
          {
            user_id: user.id,
            type,
            title,
            description,
            submitter_name: name || null,
            submitter_email: email || null,
          },
        ]);

      if (error) throw error;

      toast({
        title: "Feedback verstuurd",
        description: "Bedankt voor je feedback! We gaan ermee aan de slag.",
      });

      onOpenChange(false);
      setTitle("");
      setDescription("");
      setType("general");
      setName("");
      setEmail("");
    } catch (error) {
      toast({
        title: "Er ging iets mis",
        description: "Je feedback kon niet worden verstuurd. Probeer het later opnieuw.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Geef feedback</DialogTitle>
          <DialogDescription>
            Deel je ideeën, meld een bug of laat ons weten wat je van de tool vindt.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="type">Type feedback</Label>
            <Select value={type} onValueChange={(value: FeedbackType) => setType(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Kies een type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="feature">Feature request</SelectItem>
                <SelectItem value="bug">Bug melding</SelectItem>
                <SelectItem value="general">Algemene feedback</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Titel</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Korte beschrijving"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beschrijving</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Geef meer details..."
              required
              className="min-h-[100px]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Naam</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Je naam"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">E-mailadres</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Je e-mailadres"
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              Versturen
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
